import { useParams } from "react-router";
import { Link } from "react-router-dom";
import { usePacketListParams } from "../hooks/usePacketListParams";
import { useShadowRunPackets } from "../hooks/useShadowRunPackets";
import { PacketFilters } from "../components/PacketFilters/PacketFilters";
import { PacketTable } from "../components/PacketTable/PacketTable";
import styles from "./PacketListScreen.module.css";

interface PaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

function SimplePagination({ page, totalPages, onPageChange }: PaginationProps) {
  if (totalPages <= 1) return null;

  return (
    <nav className={styles.pagination} aria-label="Pagination">
      <button
        type="button"
        className={styles.pageButton}
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
        aria-label="Previous page"
      >
        &lsaquo;
      </button>
      <span className={styles.pageInfo}>
        Page {page} of {totalPages}
      </span>
      <button
        type="button"
        className={styles.pageButton}
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
        aria-label="Next page"
      >
        &rsaquo;
      </button>
    </nav>
  );
}

function PacketListContent({
  batchId,
  orgCategory,
  orgId,
}: {
  batchId: string;
  orgCategory: string;
  orgId: string;
}) {
  const params = usePacketListParams();

  const query = useShadowRunPackets({
    batchId,
    orgCategory,
    orgId,
    filters: {
      severityTiers:
        params.severityTiers.length > 0 ? params.severityTiers : undefined,
      component: params.component || undefined,
      payGroups: params.payGroups.length > 0 ? params.payGroups : undefined,
      payPeriodEnds:
        params.payPeriodEnds.length > 0 ? params.payPeriodEnds : undefined,
      page: params.page,
      pageSize: params.pageSize,
    },
  });

  const totalPages = query.data?.total_pages ?? 0;
  const facets = query.data?.facets;

  return (
    <>
      <PacketFilters
        severityTiers={params.severityTiers}
        component={params.component}
        payGroups={params.payGroups}
        payPeriodEnds={params.payPeriodEnds}
        payGroupFacets={facets?.pay_groups ?? []}
        payPeriodFacets={facets?.pay_periods ?? []}
        onSeverityChange={params.setSeverityTiers}
        onComponentChange={params.setComponent}
        onPayGroupsChange={params.setPayGroups}
        onPayPeriodEndsChange={params.setPayPeriodEnds}
        onClear={params.clearFilters}
        hasActiveFilters={params.hasActiveFilters}
      />

      <PacketTable
        packets={query.data?.items ?? []}
        isLoading={query.isLoading}
        isError={query.isError}
        batchId={batchId}
        orgCategory={orgCategory}
        orgId={orgId}
      />

      <SimplePagination
        page={params.page}
        totalPages={totalPages}
        onPageChange={params.setPage}
      />
    </>
  );
}

export function PacketListScreen() {
  const { batchId } = useParams<{ batchId: string }>();
  const params = usePacketListParams();

  if (!batchId) {
    return (
      <div className={styles.container}>
        <p>Missing batch ID. Please navigate from a batch detail page.</p>
      </div>
    );
  }

  if (!params.orgCategory || !params.orgId) {
    return (
      <div className={styles.container}>
        <p>
          Missing organisation parameters. Please navigate from a batch detail
          page.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/shadow-runs" className={styles.breadcrumbLink}>
          Shadow Runs
        </Link>
        <span className={styles.breadcrumbSep}>/</span>
        <Link
          to={`/shadow-runs/${batchId}?org_category=${encodeURIComponent(params.orgCategory)}&org_id=${encodeURIComponent(params.orgId)}`}
          className={styles.breadcrumbLink}
        >
          {batchId}
        </Link>
        <span className={styles.breadcrumbSep}>/</span>
        <span className={styles.breadcrumbCurrent}>Packets</span>
      </nav>

      <PacketListContent
        batchId={batchId}
        orgCategory={params.orgCategory}
        orgId={params.orgId}
      />
    </div>
  );
}
